import { FC, useContext, useState } from "react";

import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import Modal from "~/components/Modal/Modal";
import { CartLine } from "~/entities/orderEntities";
import OrderContext from "~/contexts/OrderContext";
import RemoveIcon from "~/icons/RemoveIcon";
import CartLineView from "../CartItem/CartItem";
import EmptyCartIcon from "~/icons/EmptyCartIcon";
import ThemeContext from "~/contexts/ThemeContext";
import TaxContext from "~/contexts/TaxContext";
import LoyaltyEarnings from "~/modules/loyalty/components/LoyaltyEarnings/LoyaltyEarnings";
import AuthContext from "~/contexts/AuthContext";

import styles from "./Cart.module.css";
import PromoCode from "~/modules/loyalty/components/PromoCode/PromoCode";
import AdditionalProducts from "../AdditionalProducts/AdditionalProducts";
import { getSurchargePercentage, getTotalPrice } from "~/helpers/cartHelpers";
import {
  CartItemNameFontSize,
  CartItemPriceFontSize,
} from "~/theme/models/themeModels";
import clsx from "clsx";

type CartProps = {
  title?: string;
  showAdditionalProducts: boolean;
  readOnly: boolean;
  clearable: boolean;
  hideAmountsForMobile: boolean;
  surcharge: number;
};

const Cart: FC<CartProps> = ({
  title,
  showAdditionalProducts,
  readOnly,
  clearable,
  hideAmountsForMobile,
  surcharge,
}) => {
  const [removeOrderItemId, setRemoveOrderItemId] = useState<string | null>(
    null,
  );
  const [selectedLine, setSelectedLine] = useState<CartLine | null>(null);
  const [visibleResetCartModal, setVisibleResetCartModal] = useState(false);

  const { template } = useContext(ThemeContext);
  const { formatAmount, currency } = useContext(TaxContext);

  const { orderSummary, updateOrderLine, removeOrderLine, reset, orderSource } =
    useContext(OrderContext);
  const { useComoAuth, authenticatedUser } = useContext(AuthContext);

  // Styles
  const cartBackgroundColor = template.cart.backgroundColour;
  const cartFontColor = template.cart.fontColour;

  const cartItemNameFontSize =
    CartItemNameFontSize[template.cart.item.nameFontSize];
  const cartItemNameFontColor = template.cart.item.nameFontColour;
  const cartItemPriceFontSize =
    CartItemPriceFontSize[template.cart.item.priceFontSize];
  const cartItemPriceFontColor = template.cart.item.priceFontColour;

  const getAmounts = () => {
    const amounts: {
      className: string;
      label: string;
      amount: ReturnType<typeof formatAmount>;
    }[] = [];

    for (const discount of orderSummary.discounts) {
      const discountAmount = [
        discount.percentTimes1000
          ? `-${discount.percentTimes1000 / 1000}%`
          : null,
        discount.fixedAmount ? `-${formatAmount(discount.fixedAmount)}` : null,
      ]
        .filter(Boolean)
        .join(" and ");

      amounts.push({
        className: styles.discountRow,
        label: discount.name,
        amount: discountAmount,
      });
    }

    // Surcharge
    if (surcharge) {
      amounts.push({
        className: styles.amountRow,
        label: `Surcharge ${getSurchargePercentage(
          orderSummary.finalPriceIncTax ?? 0,
          surcharge,
        )}`,
        amount: formatAmount(surcharge),
      });
    }

    // Discount
    if (
      orderSummary &&
      orderSummary.totalBeforeDiscount - orderSummary.finalPriceIncTax !== 0
    ) {
      amounts.push({
        className: styles.amountRow,
        label: "Total Discount",
        amount: formatAmount(
          orderSummary.totalBeforeDiscount - orderSummary.finalPriceIncTax,
        ),
      });
    }

    // Total
    amounts.push({
      className: styles.totalRow,
      label: `Total ${
        currency?.stripeCurrency &&
        `(${currency?.stripeCurrency.toUpperCase()})`
      }`.trim(),
      amount: formatAmount(orderSummary.finalPriceIncTax ?? 0),
    });

    return amounts;
  };

  return (
    <div
      className={styles.cart}
      style={{
        backgroundColor: cartBackgroundColor,
        color: cartFontColor,
      }}
    >
      <ProductModal
        isInsert={false}
        initLine={selectedLine}
        onClose={() => setSelectedLine(null)}
      />
      <Modal
        title="Are you sure you want to remove this item from your cart?"
        visible={!!removeOrderItemId}
        onClose={() => setRemoveOrderItemId(null)}
        onConfirm={() => {
          if (removeOrderItemId) {
            removeOrderLine(removeOrderItemId);
            setRemoveOrderItemId(null);
          }
        }}
      />
      <Modal
        title="Are you sure you want to empty your cart?"
        visible={visibleResetCartModal}
        onClose={() => setVisibleResetCartModal(false)}
        onConfirm={reset}
      />
      {orderSummary.lines.length ? (
        <>
          <div className={styles.scroll}>
            {showAdditionalProducts && <AdditionalProducts />}
            <div className={styles.title}>
              <span>{title || `My Cart (${orderSummary.lines.length})`}</span>
              {!!orderSummary.lines.length && clearable && (
                <div
                  className={styles.clearButton}
                  onClick={() => setVisibleResetCartModal(true)}
                >
                  <RemoveIcon />
                  <span>Clear cart</span>
                </div>
              )}
            </div>
            {readOnly ? (
              <div className={styles.subTotals}>
                {orderSummary.lines.map((line) => (
                  <div key={line.id} className={styles.subTotalItem}>
                    <div className={styles.subTotalRow}>
                      <span
                        className={styles.subTotalName}
                        style={{
                          fontSize: cartItemNameFontSize,
                          color: cartItemNameFontColor,
                        }}
                      >
                        {`x ${line.item.qty} ${line.item.product.name}`}
                      </span>
                      <span
                        className={styles.subTotalPrice}
                        style={{
                          fontSize: cartItemPriceFontSize,
                          color: cartItemPriceFontColor,
                        }}
                      >
                        {formatAmount(getTotalPrice(line.item))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.orderLines}>
                {orderSummary.lines.map((line) => (
                  <CartLineView
                    key={line.id}
                    line={line}
                    updateLine={(line: CartLine) => {
                      if (line.item.qty === 0) {
                        setRemoveOrderItemId(line.id);
                      } else {
                        updateOrderLine(line);
                      }
                    }}
                    openEditLineModal={() => setSelectedLine(line)}
                    orderSource={orderSource}
                  />
                ))}
              </div>
            )}
          </div>
          <div
            className={clsx(
              styles.orderAmounts,
              hideAmountsForMobile && styles.showInMobile,
            )}
          >
            {getAmounts().map(({ className, label, amount }, index) => (
              <div key={index} className={className}>
                <div>{label}</div>
                <div>{amount}</div>
              </div>
            ))}
            {authenticatedUser &&
              useComoAuth &&
              orderSummary.finalPriceIncTax > 0 && <LoyaltyEarnings />}
          </div>
          <div className={styles.promoCodeSection}>
            <PromoCode />
          </div>
        </>
      ) : (
        <>
          <div className={styles.emptyCart}>
            <EmptyCartIcon />
            <span className={styles.emptyCartTitle}>Your cart is empty</span>
            <span className={styles.emptyCartDescription}>
              Start adding your favorite items to place an order
            </span>
          </div>
          <div className={styles.promoCodeSection} style={{ marginTop: 24 }}>
            <PromoCode />
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
